import { atom, type WritableAtom } from 'jotai';
import { RESET } from 'jotai/utils';

import { getConfig } from '../../config';
import { DEFAULT_TENANT_BASE_URL } from '../pages/labs/home/constants';
import { nodesAsyncAtom } from '../pages/labs/home/hooks/useNodes';
import { relationshipsAsyncAtom } from '../pages/labs/home/hooks/useRelationships';
import { createShareableUrl } from '../pages/labs/home/hooks/useShareableLink';
import type { MockKey } from '../pages/labs/home/mock';
import { MOCKS } from '../pages/labs/home/mock';
import { executeAggRequest } from '../pages/labs/home/utils/requestUtils';
import { buildSiteAri } from '../pages/labs/home/utils/siteUtils';
import {
	ActiveErrorScenarioAtom,
	ERROR_SCENARIOS,
	simulatedQueryResponse,
} from '../pages/query-builder/errorSimulation';
import { queryBuilderNodesAtom } from '../pages/query-builder/home/hooks/queryBuilderGraphSchema';
import { queryBuilderSchemaAsyncAtom } from '../pages/query-builder/home/hooks/useQueryBuilderSchema';
import {
	QUERY_BUILDER_GRAPHQL_BASE_URL,
	getGraphQLEndpointUrl,
	JSON_CONTENT_TYPE,
} from './graphqlEndpoints';
import { generateCypherQueryFromConcreteTypes } from './GraphQLFragmentGenerator';
import {
	generateGraphQLQueryFromReturnLabels,
	convertLabelsToGraphQLTypes,
} from './GraphQLQueryFromLabels';
import {
	GraphQLSchemaErrorAtom,
	GraphQLSchemaLoadingAtom,
	GraphQLSchemaTypesByNameAtom,
	GraphQLSchemaVariantAtom,
} from './GraphQLSchemaAtoms';
import {
	AuthLoginUrlAtom,
	CypherEditorPositionAtom,
	CypherLspClientAtom,
	CypherQueryAtom,
	ExplorationCypherOverrideAtom,
	ExplorationGraphQLOverrideAtom,
	GraphQLLimitAtom,
	GraphQLQueryAtom,
	HeadersAtom,
	NetworkResultAtom,
	ParamsAtom,
	parameterRowsAtom,
	ReturnLabelsAtom,
	RoundTripMsAtom,
	RunningAtom,
	SelectedSiteCloudIdAtom,
	SemanticErrorsAtom,
	SpotlightAtom,
	TenantModeAtom,
	UserStaffIdAtom,
	parameterRowsBaseAtom,
	MockErrorsAtom,
	UseExternalGraphQLEndpointAtom,
	CyphieModeAtom,
	ObjectsQueryResultAtom,
	ObjectsQueryRunningAtom,
	QueryAbortControllerAtom,
	type ReturnLabelEntry,
	type SemanticError,
	type MockErrorType,
} from './LabsAtoms';
import { parseParameterValue } from './parameterValues';

/**
 * Extracts relationship type names from a Cypher query.
 *
 * Matches patterns like `-[r:relationship_type]->` or `-[:relationship_type]->`
 * and returns the relationship type names (underscore-delimited).
 */
function extractRelationshipTypesFromCypher(cypherQuery: string): string[] {
	// Match relationship patterns: -[var:TYPE]-> or -[:TYPE]->
	// The type can contain underscores, letters, and numbers
	const regex = /\[(?:\w+)?:([\w]+)\]/g;
	const types: string[] = [];
	let match;
	while ((match = regex.exec(cypherQuery)) !== null) {
		types.push(match[1]);
	}
	return Array.from(new Set(types));
}

/**
 * Determines the direction of a relationship pattern in a Cypher query.
 *
 * Returns 'outgoing' for `->`, 'incoming' for `<-`, or 'outgoing' as default.
 */
function inferDirectionFromCypher(cypherQuery: string, relType: string): 'outgoing' | 'incoming' {
	// Look for the incoming pattern `<-[:TYPE]-` around this relationship type
	const escapedType = relType.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	const incoming = new RegExp(`<-\\s*\\[(?:\\w+)?:${escapedType}\\]`);

	if (incoming.test(cypherQuery)) {
		return 'incoming';
	}
	// Default to outgoing (matches `-[:TYPE]->`)
	return 'outgoing';
}

/**
 * Infers target node labels from a Cypher query by extracting relationship types
 * and looking up their target types in the relationship registry.
 *
 * This is used as a fallback when the Cypher LSP cannot determine the return
 * node type (e.g. for unlabeled variables like `(entity)`).
 */
function inferTargetLabelsFromCypher(
	cypherQuery: string,
	allRelationships: Record<string, any>,
): string[] {
	const relTypes = extractRelationshipTypesFromCypher(cypherQuery);
	if (relTypes.length === 0) {
		return [];
	}

	const targetLabels: string[] = [];

	for (const relType of relTypes) {
		const direction = inferDirectionFromCypher(cypherQuery, relType);

		for (const [id, rel] of Object.entries(allRelationships) as [string, any][]) {
			const cypherRelName = (rel.name || id).replace(/-/g, '_');
			if (cypherRelName !== relType) {
				continue;
			}

			if (direction === 'outgoing') {
				// Target types when traversing outgoing
				const types = [...(rel.targetType || []), ...(rel.targetTypeName || [])];
				targetLabels.push(...types.filter((t: string) => t !== '*'));
			} else {
				// Source types when traversing incoming
				const types = [...(rel.fromType || []), ...(rel.fromTypeName || [])];
				targetLabels.push(...types.filter((t: string) => t !== '*'));
			}
			break;
		}
	}

	return Array.from(new Set(targetLabels));
}

// Helper function to wait for schema to load with timeout
async function waitForSchema(
	get: any,
	timeoutMs: number = 5000,
): Promise<{ success: boolean; typesByName: any | null; error?: string }> {
	const startTime = Date.now();

	// Poll for schema to be ready
	while (Date.now() - startTime < timeoutMs) {
		const isLoading = get(GraphQLSchemaLoadingAtom);
		const typesByName = get(GraphQLSchemaTypesByNameAtom);
		const error = get(GraphQLSchemaErrorAtom);

		// If there's an error, return immediately
		if (error) {
			return { success: false, typesByName: null, error };
		}

		// If schema is loaded, return it
		if (!isLoading && typesByName) {
			return { success: true, typesByName };
		}

		// Wait a bit before checking again
		await new Promise((resolve) => setTimeout(resolve, 100));
	}

	// Timeout reached
	return {
		success: false,
		typesByName: null,
		error: `Schema loading timeout after ${timeoutMs}ms`,
	};
}

// Helper function to compare return labels
function labelsHaveChanged(
	previous: ReturnLabelEntry[] | null,
	current: ReturnLabelEntry[] | null,
): boolean {
	// If either is null/empty, they're different if they don't both equal null
	if (!previous || previous.length === 0) {
		return !(!current || current.length === 0);
	}
	if (!current || current.length === 0) {
		return true;
	}

	// If different lengths, they've changed
	if (previous.length !== current.length) {
		return true;
	}

	// Compare each entry
	for (let i = 0; i < previous.length; i++) {
		const prev = previous[i];
		const curr = current[i];

		// Compare names
		if (prev.name !== curr.name) {
			return true;
		}

		// Compare labels arrays
		if (!prev.labels || !curr.labels) {
			if (prev.labels !== curr.labels) {
				return true;
			}
			continue;
		}

		if (prev.labels.length !== curr.labels.length) {
			return true;
		}

		// Sort and compare labels (order shouldn't matter for comparison)
		const prevSorted = [...prev.labels].sort();
		const currSorted = [...curr.labels].sort();
		for (let j = 0; j < prevSorted.length; j++) {
			if (prevSorted[j] !== currSorted[j]) {
				return true;
			}
		}
	}

	return false;
}

// Helper function to extract semantic errors from GraphQL response
function extractSemanticErrors(response: any): SemanticError[] {
	const errors: SemanticError[] = [];

	// Check if response has errors array (GraphQL error format)
	if (response?.errors && Array.isArray(response.errors)) {
		for (const error of response.errors) {
			// Look for semantic errors with line/column information
			if (error?.message && error?.extensions?.classification === 'ValidationError') {
				const semanticError: SemanticError = {
					message: error.message,
				};

				// Try to extract line and column from the message
				// Format: "Semantic Error: 4:7: Variable 'uer' is not defined."
				const match = error.message.match(/Semantic Error:\s*(\d+):(\d+):\s*(.+)/);
				if (match) {
					semanticError.line = parseInt(match[1], 10);
					semanticError.column = parseInt(match[2], 10);
					semanticError.message = match[3]; // Use the message without the prefix
				}

				errors.push(semanticError);
			}
		}
	}

	return errors;
}

/**
 * Generates mock GraphQL errors based on the selected error types
 */
function generateMockErrors(errorTypes: MockErrorType[]): any[] {
	const errors: any[] = [];

	for (const errorType of errorTypes) {
		switch (errorType) {
			case 'something-went-wrong':
				errors.push({
					message: 'Something went wrong',
					locations: [],
					extensions: {
						errorSource: 'UNDERLYING_SERVICE',
						classification: 'DataFetchingException',
						statusCode: 500,
					},
				});
				break;
			case 'null-value-non-nullable':
				errors.push({
					message:
						"The field at path '/graphStore/cypherQueryV2' was declared as a non null type, but the code involved in retrieving data has wrongly returned a null value.  The graphql specification requires that the parent field be set to null, or if that is non nullable that it bubble up null to its parent and so on. The non-nullable type is 'GraphStoreCypherQueryV2Connection' within parent type 'GraphStore'",
					locations: [],
					path: ['graphStore', 'cypherQueryV2'],
					extensions: {
						errorSource: 'UNDERLYING_SERVICE',
						classification: 'NullValueInNonNullableField',
						statusCode: 500,
					},
				});
				break;
			case 'validation-error':
				errors.push({
					message: 'This is a real error that should be displayed',
					locations: [],
					extensions: {
						classification: 'ValidationError',
					},
				});
				break;
		}
	}

	return errors;
}

// Write-only action atom that executes a query
export const ExecuteQueryAtom: WritableAtom<
	null,
	[selectedMock?: MockKey | undefined],
	Promise<void>
> & {
	init: null;
} = atom(null, async (get, set, selectedMock: MockKey = 'none') => {
	// Read previousReturnLabels BEFORE resetting to compare with current labels
	const previousReturnLabels = get(ReturnLabelsAtom);

	// Determine whether this execution is driven by an exploration action
	// (discovery / relationship loading). When true, the Objects view atoms
	// must NOT be updated — exploration manages its own loading state.
	const isExplorationQuery = get(ExplorationCypherOverrideAtom) !== null;

	// Abort any previous in-flight query and create a new controller
	const prevController = get(QueryAbortControllerAtom);
	prevController?.abort();
	const abortController = new AbortController();
	set(QueryAbortControllerAtom, abortController);

	set(RunningAtom, true);
	if (!isExplorationQuery) {
		set(ObjectsQueryRunningAtom, true);
	}
	set(NetworkResultAtom, RESET);
	set(ReturnLabelsAtom, RESET);
	set(AuthLoginUrlAtom, null);
	set(RoundTripMsAtom, null);
	set(SemanticErrorsAtom, RESET);

	let startedAt = 0;
	try {
		const scenario = get(UseExternalGraphQLEndpointAtom) ? get(ActiveErrorScenarioAtom) : 'none';
		if (scenario !== 'none') {
			const surface = ERROR_SCENARIOS.find((option) => option.value === scenario)?.surface;
			if (surface === 'execution') {
				const response = simulatedQueryResponse(scenario, MOCKS['mock-cypher-result'].data?.data);
				set(NetworkResultAtom, response);
				if (!isExplorationQuery) set(ObjectsQueryResultAtom, response);
				if (scenario === 'signIn') set(AuthLoginUrlAtom, 'simulated');
				set(RoundTripMsAtom, 0);
			}
			return;
		}
		// Read atoms at execution time without creating subscriptions.
		// If an exploration override is set, use it instead of the editor's
		// CypherQueryAtom so the visible editor text is never affected.
		const cypherOverride = get(ExplorationCypherOverrideAtom);
		const graphQLOverride = get(ExplorationGraphQLOverrideAtom);
		const cypherQuery = cypherOverride ?? get(CypherQueryAtom);
		let graphQLQuery = graphQLOverride ?? get(GraphQLQueryAtom);
		const headers = get(HeadersAtom);
		for (const row of get(parameterRowsAtom)) {
			if (row.type !== 'parameter' || !row.enabled || !row.name.trim()) continue;
			try {
				parseParameterValue(row.value, row.valueType);
			} catch (error) {
				throw new Error(
					`Semantic Error: $${row.name}: ${error instanceof Error ? error.message : 'Invalid parameter value.'}`,
				);
			}
		}
		const params = get(ParamsAtom);
		const tenantMode = get(TenantModeAtom);
		const selectedSiteCloudId = get(SelectedSiteCloudIdAtom);
		const parameterRows = get(parameterRowsBaseAtom);

		// Always use DEFAULT_TENANT_BASE_URL for routing GraphQL requests
		const aggBaseUrl = DEFAULT_TENANT_BASE_URL;

		const isCypherEmpty = !cypherQuery.trim();
		const isGraphQLEmpty = !graphQLQuery.trim();

		if (isCypherEmpty) {
			set(SpotlightAtom, 'cypher');
			set(RunningAtom, false);
			return;
		}

		if (selectedMock !== 'none') {
			set(NetworkResultAtom, MOCKS[selectedMock].data);
			if (!isExplorationQuery) {
				set(ObjectsQueryResultAtom, MOCKS[selectedMock].data);
			}
			set(RoundTripMsAtom, 0);
			return;
		}

		// Get current return labels from the Cypher query via the LSP.
		// The LSP client may not be initialized yet if the user triggers
		// execution before the Cypher editor / WASM worker has finished
		// loading (e.g. clicking a golden prompt on the landing page or
		// the "Explore Graph" button). In that case, wait for the LSP
		// client atom to be populated — the loading screen is already
		// shown via RunningAtom — then use the LSP to parse return labels.
		// This avoids a brittle regex fallback and ensures complex queries
		// are handled correctly by the real Cypher parser.
		let client = get(CypherLspClientAtom);
		const editorPosition = get(CypherEditorPositionAtom);
		let currentReturnLabels: ReturnLabelEntry[] | null = null;

		if (!client) {
			console.debug('[ExecuteQuery] LSP client not ready, waiting...');
			const lspStartTime = Date.now();
			const lspTimeoutMs = 8000;
			while (Date.now() - lspStartTime < lspTimeoutMs) {
				await new Promise((resolve) => setTimeout(resolve, 200));
				client = get(CypherLspClientAtom);
				if (client) {
					console.debug('[ExecuteQuery] LSP client became available');
					break;
				}
			}
			if (!client) {
				console.warn(
					'[ExecuteQuery] LSP client not available after timeout, proceeding with inference fallback',
				);
			}
		}

		if (client) {
			try {
				await client.ready;
				// Use the editor cursor position if available, otherwise
				// default to (1, 1) for programmatic executions where the
				// user hasn't interacted with the editor yet.
				const line = editorPosition?.line ?? 1;
				const column = editorPosition?.column ?? 1;
				currentReturnLabels = await client.notifyOnReturn(line, column);
			} catch (error) {
				console.error('[ExecuteQuery] Failed to get return labels from LSP:', error);
			}
		}

		// Check if any of the current return labels actually have concrete labels
		// (not just variable names with empty label arrays). When the LSP can't
		// determine the node type (e.g. unlabeled variables like `(entity)`),
		// the labels array is empty and we can't generate a GraphQL query from them.
		const hasConcreteLabels =
			currentReturnLabels != null &&
			currentReturnLabels.length > 0 &&
			currentReturnLabels.some((entry) => entry.labels && entry.labels.length > 0);

		const cyphieModeSuffixes = {
			default: '_CyphieDefault',
			reasoning: '_CyphieReasoning',
		} as const;
		const cyphieMode = get(CyphieModeAtom);
		const cyphieQueryNameSuffix = cyphieMode ? cyphieModeSuffixes[cyphieMode] : undefined;

		// When an exploration action provides a non-empty GraphQL override,
		// use it directly. Exploration actions generate correct fragments for
		// their target types; regenerating from the editor's LSP labels would
		// overwrite them with fragments for the wrong types. This also avoids
		// a race when parallel discovery queries reset ReturnLabelsAtom,
		// causing labelsHaveChanged to be spuriously true.
		const hasExplorationGraphQL =
			isExplorationQuery && typeof graphQLOverride === 'string' && graphQLOverride.trim() !== '';

		// Determine if we should regenerate the GraphQL query
		const shouldRegenerate =
			!hasExplorationGraphQL &&
			(isGraphQLEmpty || labelsHaveChanged(previousReturnLabels, currentReturnLabels)) &&
			hasConcreteLabels;

		if (shouldRegenerate) {
			console.debug('[ExecuteQuery] Return labels changed or GraphQL empty, regenerating query');

			// Generate GraphQL query from the labels we already have
			if (currentReturnLabels && currentReturnLabels.length > 0) {
				try {
					const nodesMap = get(UseExternalGraphQLEndpointAtom)
						? await get(queryBuilderNodesAtom)
						: await get(nodesAsyncAtom);

					if (nodesMap) {
						// Wait for schema to be loaded (with timeout to prevent hanging)
						console.debug('[ExecuteQuery] Waiting for GraphQL schema to load...');
						const schemaResult = await waitForSchema(get, 5000);

						if (schemaResult.success) {
							console.debug('[ExecuteQuery] GraphQL schema loaded successfully');
							const userStaffId = get(UserStaffIdAtom);
							const graphQLLimit = get(GraphQLLimitAtom);
							const schemaVariant = get(GraphQLSchemaVariantAtom);
							const queryResult = generateGraphQLQueryFromReturnLabels(
								currentReturnLabels,
								nodesMap,
								schemaResult.typesByName,
								graphQLLimit,
								userStaffId || undefined,
								schemaVariant,
								cyphieQueryNameSuffix,
							);

							if (queryResult) {
								if (queryResult.warnings.length > 0) {
									console.warn(
										'[ExecuteQuery] GraphQL query generation warnings:',
										queryResult.warnings,
									);
								}
								console.debug(
									'[ExecuteQuery] Successfully generated GraphQL query, updating editor',
								);
								set(GraphQLQueryAtom, queryResult.query);
								set(ReturnLabelsAtom, currentReturnLabels);
								graphQLQuery = queryResult.query;
							} else {
								console.warn(
									'[ExecuteQuery] Failed to generate query from labels:',
									currentReturnLabels,
								);
							}
						} else {
							console.error('[ExecuteQuery] Failed to load GraphQL schema:', schemaResult.error);
						}
					} else {
						console.warn('[ExecuteQuery] Missing nodesMap - cannot generate GraphQL query');
					}
				} catch (error) {
					console.error('[ExecuteQuery] Error generating GraphQL query:', error);
				}
			}

			// Check if generation succeeded
			if (!graphQLQuery.trim()) {
				if (isGraphQLEmpty) {
					console.warn('[ExecuteQuery] No GraphQL query generated and no existing query found');
					if (get(UseExternalGraphQLEndpointAtom)) {
						set(NetworkResultAtom, { error: 'Unable to prepare query' });
					} else {
						set(SpotlightAtom, 'graphql');
					}
					set(RunningAtom, false);
					return;
				}
				console.debug('[ExecuteQuery] Using existing GraphQL query as fallback');
			}
		} else {
			console.debug('[ExecuteQuery] Return labels unchanged, keeping existing GraphQL query');
			// Still update the ReturnLabelsAtom for consistency
			set(ReturnLabelsAtom, currentReturnLabels);

			// If there is no existing GraphQL query and we can't generate one
			// from labels, try to infer target types from relationship types
			// in the Cypher query as a fallback
			if (isGraphQLEmpty) {
				console.debug(
					'[ExecuteQuery] No existing GraphQL query, attempting to infer types from Cypher relationships',
				);

				let inferred = false;
				try {
					const external = get(UseExternalGraphQLEndpointAtom);
					const relRegistry = external
						? Object.fromEntries(
								((await get(queryBuilderSchemaAsyncAtom)).relationshipTypes ?? []).map((rel) => [
									rel.name,
									{ name: rel.name, fromType: rel.fromLabel, targetType: rel.targetLabel },
								]),
							)
						: await get(relationshipsAsyncAtom);
					if (relRegistry) {
						const inferredLabels = inferTargetLabelsFromCypher(cypherQuery, relRegistry);
						if (inferredLabels.length > 0) {
							const nodesMap = external
								? await get(queryBuilderNodesAtom)
								: await get(nodesAsyncAtom);
							if (nodesMap) {
								const graphQLTypes = convertLabelsToGraphQLTypes(
									inferredLabels,
									nodesMap,
									external ? 'external' : 'internal',
								);
								if (graphQLTypes.length > 0) {
									const schemaResult = await waitForSchema(get, 5000);
									const resolvedGraphQLTypes = external
										? convertLabelsToGraphQLTypes(
												inferredLabels,
												nodesMap,
												'external',
												schemaResult.typesByName ?? undefined,
											)
										: graphQLTypes;
									if (schemaResult.success && resolvedGraphQLTypes.length > 0) {
										const graphQLLimit = get(GraphQLLimitAtom);
										const userStaffId = get(UserStaffIdAtom);
										const queryResult = generateCypherQueryFromConcreteTypes(
											resolvedGraphQLTypes,
											schemaResult.typesByName,
											{
												depth: 2,
												includeTypename: true,
												skipDeprecated: true,
												skipExperimental: true,
												autoSelectIdForObjects: true,
												queryNameSuffix: cyphieQueryNameSuffix,
												schemaVariant: external ? 'external' : 'internal',
											},
											graphQLLimit,
											userStaffId || undefined,
										);
										if (queryResult) {
											console.debug(
												'[ExecuteQuery] Successfully inferred GraphQL query from Cypher relationship types:',
												resolvedGraphQLTypes,
											);
											set(GraphQLQueryAtom, queryResult.query);
											graphQLQuery = queryResult.query;
											inferred = true;
										}
									}
								}
							}
						}
					}
				} catch (error) {
					console.error('[ExecuteQuery] Error inferring types from Cypher:', error);
				}

				if (!inferred) {
					console.warn(
						'[ExecuteQuery] Could not infer GraphQL query. Please provide one manually.',
					);
					if (get(UseExternalGraphQLEndpointAtom)) {
						set(NetworkResultAtom, { error: 'Unable to prepare query' });
					} else {
						set(SpotlightAtom, 'graphql');
					}
					set(RunningAtom, false);
					return;
				}
			}
		}

		set(SpotlightAtom, RESET);

		const useExternalEndpoint = get(UseExternalGraphQLEndpointAtom);
		const graphqlPath = useExternalEndpoint ? '/gateway/api/graphql/twg' : '/gateway/api/graphql';
		const requestBaseUrl = useExternalEndpoint ? QUERY_BUILDER_GRAPHQL_BASE_URL : aggBaseUrl;
		const url = getGraphQLEndpointUrl(graphqlPath, requestBaseUrl);

		console.debug('[ExecuteQuery] Request URL:', url);
		const defaultHeaders = {
			accept: 'application/json, multipart/mixed, multipart/mixed; deferSpec=20220824',
			'content-type': JSON_CONTENT_TYPE,
			'atl-agg-debug': 'true',
		} as Record<string, string>;

		if (!useExternalEndpoint) {
			const xQueryContextRow = Object.values(parameterRows).find(
				(row) => row.type === 'header' && row.name.trim() === 'X-Query-Context',
			);
			const isXQueryContextExplicitlyDisabled = xQueryContextRow && !xQueryContextRow.enabled;

			if (!isXQueryContextExplicitlyDisabled) {
				let cloudIdForHeader: string | null = null;

				if (tenantMode === 'select-site' && selectedSiteCloudId) {
					cloudIdForHeader = selectedSiteCloudId;
				} else if (tenantMode === 'default') {
					const { defaultSiteCloudId } = getConfig();
					cloudIdForHeader = defaultSiteCloudId;
				}

				if (cloudIdForHeader) {
					const ari = buildSiteAri(cloudIdForHeader);
					defaultHeaders['X-Query-Context'] = ari;
					console.debug(
						'[ExecuteQuery] Adding X-Query-Context header:',
						ari,
						'for mode:',
						tenantMode,
					);
				}
			} else {
				console.debug('[ExecuteQuery] X-Query-Context header explicitly disabled by user');
			}
		}

		// The generated GraphQL query expects (single query API):
		// - cypherQuery: the Cypher query string
		// - params: parameter object (optional)
		// - version: the API version (V3)
		const variables = {
			cypherQuery: cypherQuery,
			params: params || {},
			version: 'V3',
		};

		const operationNameMatch = graphQLQuery.match(/(?:query|mutation)\s+(\w+)/);
		const operationName = operationNameMatch ? operationNameMatch[1] : undefined;

		const requestBody = {
			query: graphQLQuery,
			variables,
			...(operationName && { operationName }),
		};

		const finalHeaders = { ...defaultHeaders, ...headers };
		if (useExternalEndpoint) {
			Object.keys(finalHeaders).forEach((headerName) => {
				if (headerName.toLowerCase() === 'x-query-context') {
					delete finalHeaders[headerName];
				}
			});
			if (selectedSiteCloudId) {
				finalHeaders['X-Query-Context'] = buildSiteAri(selectedSiteCloudId);
			}
		}
		console.debug('[ExecuteQuery] Final headers:', finalHeaders);

		startedAt = performance.now();
		const res = await executeAggRequest({
			url,
			headers: finalHeaders,
			body: requestBody,
			timeoutMs: 30000,
			customerErrors: useExternalEndpoint,
			signal: abortController.signal,
		});
		set(RoundTripMsAtom, Math.max(0, Math.round(performance.now() - startedAt)));

		// Inject mock errors if any are selected (replace any existing errors)
		const mockErrors = get(MockErrorsAtom);
		let finalResponse = res;
		if (mockErrors.length > 0) {
			const mockErrorObjects = generateMockErrors(mockErrors);
			finalResponse = {
				...res,
				errors: mockErrorObjects,
			};
		}
		set(NetworkResultAtom, finalResponse);
		if (!isExplorationQuery) {
			set(ObjectsQueryResultAtom, finalResponse);
		}

		// Detect UNAUTHENTICATED auth error and show login banner
		const unauth =
			Array.isArray(finalResponse?.errors) &&
			finalResponse.errors.some(
				(e: any) =>
					e?.extensions?.presentedAuth === 'UNAUTHENTICATED' ||
					(e?.extensions?.statusCode === 403 &&
						e?.extensions?.classification === 'IncorrectAuthException') ||
					/UNAUTHENTICATED/i.test(String(e?.message ?? '')),
			);
		if (unauth) {
			const base = requestBaseUrl.replace(/\/+$/, '');
			set(AuthLoginUrlAtom, `${base}${graphqlPath}`);
		} else {
			set(AuthLoginUrlAtom, null);
		}

		// Extract and store semantic errors
		const semanticErrors = extractSemanticErrors(finalResponse);
		if (semanticErrors.length > 0) {
			console.debug('[ExecuteQuery] Found semantic errors:', semanticErrors);
			set(SemanticErrorsAtom, semanticErrors);
		}
	} catch (e) {
		// If the request was aborted (e.g. by undo), exit silently without
		// overwriting atoms — the undo handler already restored the correct state.
		if (abortController.signal.aborted) {
			return;
		}
		if (startedAt) {
			set(RoundTripMsAtom, Math.max(0, Math.round(performance.now() - startedAt)));
		}
		const errorResult =
			e instanceof Error ? { error: e.message, name: e.name } : { error: String(e) };
		set(NetworkResultAtom, errorResult);
		if (!isExplorationQuery) {
			set(ObjectsQueryResultAtom, errorResult);
		}
	} finally {
		// Skip cleanup if aborted — undo handler manages state.
		if (!abortController.signal.aborted) {
			set(RunningAtom, false);
			if (!isExplorationQuery) {
				set(ObjectsQueryRunningAtom, false);
			}
		}
	}
});

// Write-only action atom that copies the current state as a shareable link
export const CopyShareableLinkAtom: WritableAtom<null, [], void> & {
	init: null;
} = atom(null, (get) => {
	// Read atoms at execution time without creating subscriptions
	const tenantMode = get(TenantModeAtom);
	const selectedSiteCloudId = get(SelectedSiteCloudIdAtom);
	const cypherQuery = get(CypherQueryAtom);
	const headers = get(HeadersAtom);
	const params = get(ParamsAtom);

	// Construct tenant parameter based on mode
	let tenant: string | undefined;
	if (window.location.pathname.startsWith('/query-builder') && selectedSiteCloudId) {
		tenant = selectedSiteCloudId;
	} else if (tenantMode === 'default') {
		tenant = 'default';
	} else if (tenantMode === 'select-site' && selectedSiteCloudId) {
		tenant = selectedSiteCloudId;
	}

	const url = createShareableUrl({
		tenant,
		cypher: cypherQuery,
		headers,
		params,
	});

	navigator.clipboard.writeText(url);
});
